import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { NavBar } from "@/components/navbar";
import {Footer} from "@/components/SiteFooter";
import { motion, useInView, useAnimation } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { 
  Search, 
  ChevronRight, 
  BookOpen, 
  Users, 
  Settings, 
  HelpCircle, 
  FileText, 
  Mail, 
  Phone, 
  MessageCircle, 
  BookMarked,
  Lightbulb,
  LifeBuoy
} from "lucide-react";

const HelpCenterPage = () => {
  const { t } = useTranslation("common");
  
  // Animation controls for scroll animations
  const controls = useAnimation();
  const resourcesRef = useRef(null);
  const resourcesInView = useInView(resourcesRef, { once: true });
  
  useEffect(() => {
    document.title = "Help Center | Verdea";
    
    if (resourcesInView) {
      controls.start("visible");
    }
  }, [controls, resourcesInView]);

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
  };

  const helpTopics = [
    {
      title: "Getting Started",
      description: "Learn the basics of using Verdea for CSRD compliance reporting",
      icon: BookOpen,
      link: "/help/getting-started",
      articles: [
        "Platform overview",
        "Creating your first report",
        "Understanding the dashboard",
        "Importing your data"
      ]
    },
    {
      title: "Account Management",
      description: "Manage your account settings, users, and permissions",
      icon: Users,
      link: "/help/account-management",
      articles: [
        "User roles and permissions",
        "Adding team members",
        "Security settings",
        "Subscription management"
      ]
    },
    {
      title: "Troubleshooting",
      description: "Solutions for common issues and technical questions",
      icon: HelpCircle,
      link: "/help/troubleshooting",
      articles: [
        "Login issues",
        "Data import problems",
        "Report generation errors",
        "Integration troubleshooting"
      ]
    }
  ];

  const supportResources = [
    {
      title: "Knowledge Base",
      description: "Browse our comprehensive library of help articles and guides",
      icon: BookMarked,
      link: "/documentation"
    },
    {
      title: "Video Tutorials",
      description: "Step-by-step video guides for using Verdea effectively",
      icon: Lightbulb,
      link: "/documentation#tutorials"
    },
    {
      title: "Support Portal",
      description: "Submit and track your support tickets in our dedicated portal",
      icon: LifeBuoy,
      link: "https://support.verdea.com"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Help Center | Verdea</title>
        <meta name="description" content="Get help with Verdea's CSRD compliance platform. Browse guides, tutorials, and FAQs or contact our support team for assistance." />
      </Helmet>
      
      <NavBar />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-emerald-50 to-white pt-24 pb-16">
        <div className="absolute inset-0 z-0 opacity-40">
          <svg className="h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <defs>
              <pattern id="grid" width="8" height="8" patternUnits="userSpaceOnUse">
                <path d="M 8 0 L 0 0 0 8" fill="none" stroke="rgba(15, 61, 43, 0.05)" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-br from-[#1A1F2C] to-[#0F3D2B] bg-clip-text text-transparent">
              Help Center
            </h1>
            <p className="text-xl text-slate-700 mb-8 leading-relaxed">
              Find answers, guides, and support for your CSRD compliance journey with Verdea
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="max-w-2xl mx-auto relative"
          >
            <div className="relative mb-12">
              <Input
                type="search"
                placeholder="Search for help articles, guides, and FAQs..."
                className="w-full h-14 pl-12 pr-4 rounded-xl shadow-sm"
              />
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-emerald-700" size={20} />
              <Button 
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-emerald-700 hover:bg-emerald-800 text-white h-10 px-4"
                size="sm"
              >
                Search
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Help Topics Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-slate-900">
              Popular Help Topics
            </h2>
            <p className="text-lg text-slate-600">
              Browse guides and resources for the most common questions
            </p>
          </motion.div>

          <div className="max-w-5xl mx-auto">
            <div className="relative">
              {/* Apple-style Tabs */}
              <Tabs defaultValue={helpTopics[0].title.toLowerCase().replace(/\s+/g, '-')} className="w-full">
                {/* Apple-inspired Tab Navigation */}
                <div className="relative flex justify-center mb-16">
                  <div className="absolute bottom-0 w-full border-b border-gray-200"></div>
                  <TabsList className="relative bg-transparent border-0 flex space-x-8 p-0 z-10">
                    {helpTopics.map((topic) => (
                      <TabsTrigger 
                        key={topic.title}
                        value={topic.title.toLowerCase().replace(/\s+/g, '-')}
                        className="px-1 py-2 text-base font-medium text-gray-500 border-b-2 border-transparent data-[state=active]:border-emerald-600 data-[state=active]:text-emerald-700 transition-all duration-200 bg-transparent hover:text-emerald-600 rounded-none focus-visible:outline-none focus-visible:ring-0"
                      >
                        {topic.title}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </div>
                
                {/* Tab Content with Apple-like Styling */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                  className="relative z-0"
                >
                  {helpTopics.map((topic) => (
                    <TabsContent 
                      key={topic.title} 
                      value={topic.title.toLowerCase().replace(/\s+/g, '-')} 
                      className="mt-0 w-full"
                    >
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="bg-white rounded-2xl p-8 shadow-[0_10px_60px_-15px_rgba(0,0,0,0.1)] border border-gray-100"
                      >
                        <div className="grid md:grid-cols-2 gap-12">
                          <div>
                            <div className="flex items-center gap-3 mb-4">
                              <div className="p-2 bg-emerald-100 rounded-lg">
                                <topic.icon className="h-6 w-6 text-emerald-700" />
                              </div>
                              <h3 className="text-2xl font-semibold text-slate-900">{topic.title}</h3>
                            </div>
                            <p className="text-slate-600 mb-6 leading-relaxed">
                              {topic.description}
                            </p>
                            <div className="space-y-3 mb-6">
                              <h4 className="text-sm font-medium text-slate-500 uppercase tracking-wider">Popular Articles</h4>
                              <ul className="space-y-2">
                                {topic.articles.map((article, i) => (
                                  <li key={i} className="flex items-start">
                                    <div className="h-1 w-1 bg-emerald-500 rounded-full mt-3 mr-2"></div>
                                    <Link to={`${topic.link}#${article.toLowerCase().replace(/\s+/g, '-')}`} className="text-slate-700 hover:text-emerald-700 transition-colors">
                                      {article}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <Link to={topic.link}>
                              <Button className="bg-emerald-700 hover:bg-emerald-800">
                                Browse All Articles
                                <ChevronRight className="ml-2 h-4 w-4" />
                              </Button>
                            </Link>
                          </div>
                          <div className="relative rounded-xl overflow-hidden shadow-sm bg-slate-50 p-1">
                            <img 
                              src={`/screens/${topic.title.toLowerCase().replace(/\s+/g, '_')}.png`} 
                              alt={topic.title} 
                              onError={(e) => {
                                e.currentTarget.src = "/screens/csrdatlas_knowledge_base.png";
                              }}
                              className="w-full h-full object-cover rounded-lg"
                            />
                          </div>
                        </div>
                      </motion.div>
                    </TabsContent>
                  ))}
                </motion.div>
              </Tabs>
            </div>
          </div>
        </div>
      </section>

      {/* Support Resources Section */}
      <section ref={resourcesRef} className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-slate-900">
              Support Resources
            </h2>
            <p className="text-lg text-slate-600">
              Additional ways to find help and learn more about Verdea
            </p>
          </motion.div>

          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate={controls}
            className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto"
          >
            {supportResources.map((resource, index) => {
              const Icon = resource.icon;
              return (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow border border-slate-200"
                >
                  <div className="mb-4 p-3 bg-emerald-50 w-fit rounded-lg">
                    <Icon className="h-7 w-7 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-slate-900">{resource.title}</h3>
                  <p className="text-slate-600 mb-6">{resource.description}</p>
                  <Link to={resource.link} className="text-emerald-700 font-medium hover:text-emerald-800 flex items-center">
                    Explore
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </Link>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Contact Support Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl mx-auto"
          >
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold mb-6 text-slate-900">Need More Help?</h2>
                <p className="text-slate-600 mb-8">
                  Our support team is available to assist you with any questions or issues you may have with the Verdea platform.
                </p>
                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="p-2 bg-emerald-100 rounded-lg mr-4">
                      <Mail className="h-5 w-5 text-emerald-700" />
                    </div>
                    <div>
                      <h3 className="text-lg font-medium mb-1 text-slate-900">Email Support</h3>
                      <p className="text-slate-600">support@verdea.com</p>
                      <p className="text-sm text-slate-500 mt-1">Response within 24 hours</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="p-2 bg-emerald-100 rounded-lg mr-4">
                      <Phone className="h-5 w-5 text-emerald-700" />
                    </div>
                    <div>
                      <h3 className="text-lg font-medium mb-1 text-slate-900">Phone Support</h3>
                      <p className="text-slate-600">+49 (0) 30 12345678</p>
                      <p className="text-sm text-slate-500 mt-1">Monday-Friday, 9am-6pm CET</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="p-2 bg-emerald-100 rounded-lg mr-4">
                      <MessageCircle className="h-5 w-5 text-emerald-700" />
                    </div>
                    <div>
                      <h3 className="text-lg font-medium mb-1 text-slate-900">Live Chat</h3>
                      <p className="text-slate-600">Available in your Verdea dashboard</p>
                      <p className="text-sm text-slate-500 mt-1">Business hours only</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-emerald-50 rounded-2xl p-8 border border-emerald-100">
                <h3 className="text-xl font-semibold mb-4 text-emerald-800">Enterprise Support</h3>
                <p className="text-emerald-700 mb-6">
                  Enterprise clients receive priority support with dedicated account managers and faster response times.
                </p>
                <Link to="/enterprise">
                  <Button className="bg-emerald-700 hover:bg-emerald-800 w-full">
                    Learn About Enterprise Options
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-16 bg-emerald-700 text-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="text-3xl font-bold mb-4">
              Can't find what you're looking for?
            </h2>
            <p className="text-lg text-emerald-100 mb-8 max-w-2xl mx-auto">
              Contact our support team directly for personalized assistance
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-emerald-700 hover:bg-emerald-50">
                Contact Support
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HelpCenterPage;
